import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from "react";
import { shoppingCentresService } from "@/api/services/shoppingCentres.service";
import { useShoppingCentreDisplay } from "@/hooks/useShoppingCentreDisplay";
import styles from "./SearchableShoppingCentreSelect.module.css";
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 50;
export const SearchableShoppingCentreSelect = ({ value, onChange, valueMode = "id", placeholder = "Search shopping centres...", disabled = false, }) => {
    const containerRef = useRef(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [centres, setCentres] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const displayValue = useShoppingCentreDisplay(value || "");
    useEffect(() => {
        if (!showDropdown)
            return;
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchTerm, showDropdown]);
    useEffect(() => {
        if (!showDropdown)
            return;
        const fetchCentres = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await shoppingCentresService.getShoppingCentres({
                    search: debouncedSearch || undefined,
                    pageSize: PAGE_SIZE,
                });
                setCentres(response.data.data || []);
            }
            catch (err) {
                console.error("Failed to fetch shopping centres:", err);
                setError("Failed to load shopping centres");
                setCentres([]);
            }
            finally {
                setLoading(false);
            }
        };
        fetchCentres();
    }, [showDropdown, debouncedSearch]);
    useEffect(() => {
        if (!showDropdown)
            return;
        const container = containerRef.current;
        if (!container)
            return;
        const handleClickOutside = (event) => {
            if (container && !container.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showDropdown]);
    const handleFocus = useCallback(() => {
        if (disabled)
            return;
        setShowDropdown(true);
        setSearchTerm(displayValue);
    }, [disabled, displayValue]);
    const handleBlur = useCallback(() => {
        setTimeout(() => {
            setSearchTerm(displayValue);
        }, 200);
    }, [displayValue]);
    const handleSelect = useCallback((centre) => {
        if (valueMode === "id") {
            onChange(centre.id);
            setSearchTerm(centre.scName || centre.id);
        }
        else {
            onChange(centre.scName || centre.id);
            setSearchTerm(centre.scName || centre.id);
        }
        setShowDropdown(false);
    }, [onChange, valueMode]);
    const handleClear = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled)
            return;
        onChange("");
        setSearchTerm("");
        setShowDropdown(false);
    }, [disabled, onChange]);
    const inputValue = showDropdown ? searchTerm : displayValue;
    return (_jsxs("div", { ref: containerRef, className: styles.container, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.inputWrapper, children: [_jsx("input", { type: "text", className: styles.input, value: inputValue, onChange: (e) => {
                            setSearchTerm(e.target.value);
                            setShowDropdown(true);
                        }, onFocus: handleFocus, onBlur: handleBlur, placeholder: placeholder, disabled: disabled, readOnly: !showDropdown }), value && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClear, disabled: disabled, "aria-label": "Clear selection", children: "\u00D7" }))] }), showDropdown && (_jsx("div", { className: styles.dropdown, children: loading ? (_jsx("div", { className: styles.loadingText, children: "Loading shopping centres..." })) : error ? (_jsx("div", { className: styles.noResultsText, children: error })) : centres.length > 0 ? (centres.map((centre) => (_jsxs("div", { className: styles.option, onClick: () => handleSelect(centre), children: [_jsx("span", { className: styles.optionName, children: centre.scName || "Unnamed" }), centre.state && (_jsx("span", { className: styles.optionDetail, children: centre.state }))] }, centre.id)))) : (_jsx("div", { className: styles.noResultsText, children: debouncedSearch
                        ? "No matching shopping centres found"
                        : "Type to search shopping centres" })) }))] }));
};
